import LangEnum from '../../../../../../../../../lang/LangEnum';
import {
  useSyncDynamicCatalog,
  useUpdateCatalog,
} from '../../../../../../../../../services/modules/catalogs/catalogs.mutations';
import snackbarStore from '../../../../../../../../../stores/Snackbar.store';
import { AvailabilityEnum } from '../../../../../../../../products/ProductTypes';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { useAbility } from '../../../../../../../../users/AbilityContext';
import { RoleBehaviorCapability } from '../../../../../../../../users/ability.types';
import { CATALOG_CREATION_CRITERIA_CONFIGS } from '../../../../../../../creation/catalogCreationCriteria.consts';
import {
  CatalogCreationCriterionEnum,
  CatalogCreationCriterionOptionLabelsById,
  CatalogCriterionChange,
} from '../../../../../../../creation/catalogCreationCriteria.types';
import { findCatalogCreationCriterionConfig } from '../../../../../../../creation/catalogCreationCriteria.utils';
import { useCatalogCreationCriteriaOptionLabels } from '../../../../../../../creation/hooks/useCatalogCreationCriteriaOptionLabels';
import { useCatalogCriteriaEditor } from '../../../../../../../creation/hooks/useCatalogCriteriaEditor';
import { CriteriaOperatorEnum } from '../../../../../../../enums/criteriaOperator.enum';
import { CatalogAutoInclusionSectionProps } from '../CatalogAutoInclusionSection';

const useCatalogAutoInclusionSection = ({
  id,
  isReadOnly,
  availability,
  collections,
  classifications,
  availabilityCriteriaOperator,
  classificationCriteriaOperator,
}: CatalogAutoInclusionSectionProps) => {
  const { translate } = useTranslation();
  const ability = useAbility();
  const { mutate } = useUpdateCatalog({ catalogId: id });
  const { mutate: syncCatalog, isPending: isSyncing } = useSyncDynamicCatalog({
    catalogId: id,
  });
  const { optionLabelsById, registerOptionLabels } = useCatalogCreationCriteriaOptionLabels();

  const criteriaValues = {
    availability: availability ?? [],
    collectionIds: collections.map((collection) => collection.id),
    classificationIds: (classifications ?? []).map((classification) => classification.id),
  };

  const catalogOptionLabels: CatalogCreationCriterionOptionLabelsById = {
    ...Object.fromEntries(
      collections.map((collection) => [collection.id, { name: collection.name }]),
    ),
    ...Object.fromEntries(
      (classifications ?? []).map((classification) => [
        classification.id,
        {
          name: classification.name,
          description: classification.classificationType?.name,
          groupId: classification.classificationType?.id,
        },
      ]),
    ),
    ...optionLabelsById,
  };

  const clearsEveryCriterion = ({
    criterion,
    values,
  }: Pick<CatalogCriterionChange, 'criterion' | 'values'>) => {
    if (values.length > 0) {
      return false;
    }

    const { valuesField } = findCatalogCreationCriterionConfig(criterion);
    const remainingValues = { ...criteriaValues, [valuesField]: [] };

    return Object.values(remainingValues).every((criterionValues) => criterionValues.length === 0);
  };

  const changeCriterion = ({ criterion, values, operator }: CatalogCriterionChange) => {
    if (clearsEveryCriterion({ criterion, values })) {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.CATALOGS_CATALOG_FORM_CRITERIA),
      });
      return;
    }

    if (criterion === CatalogCreationCriterionEnum.AVAILABILITY) {
      mutate({
        availability: values as AvailabilityEnum[],
        availabilityCriteriaOperator: operator,
      });
      return;
    }

    if (criterion === CatalogCreationCriterionEnum.COLLECTION) {
      mutate({ collections: values });
      return;
    }

    mutate({ classifications: values, classificationCriteriaOperator: operator });
  };

  const editor = useCatalogCriteriaEditor({
    values: criteriaValues,
    operators: {
      availabilityCriteriaOperator: availabilityCriteriaOperator ?? CriteriaOperatorEnum.OR,
      classificationCriteriaOperator: classificationCriteriaOperator ?? CriteriaOperatorEnum.OR,
    },
    onRegisterOptionLabels: registerOptionLabels,
    onChangeCriterion: changeCriterion,
  });

  return {
    ...editor,
    optionLabelsById: catalogOptionLabels,
    isDisabled: isReadOnly,
    canAddCriterion: editor.chips.length < CATALOG_CREATION_CRITERIA_CONFIGS.length,
    canSyncCatalog:
      !isReadOnly && ability.hasCapability(RoleBehaviorCapability.CATALOGS_MANAGE_BRAND_CATALOGS),
    isSyncing,
    syncCatalog,
  };
};

export default useCatalogAutoInclusionSection;
